/* eslint-disable react/no-multi-comp */
import { createAction } from '@reduxjs/toolkit';
import { WebserviceProjectResponse } from '@yandex-int/k-common/client/api/defs/get-webservice-project';
import { Pane, postStatic } from '@yandex-int/k-common/client/api/defs/post-static';
import { putWebShare } from '@yandex-int/k-common/client/api/defs/put-web-share';
import cx from 'classnames';
import { Link as LinkIcon, Refresh, ShareArrow } from 'education-icons';
import { noop, pickBy } from 'lodash';
import mustache from 'mustache';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAsync, useAsyncFn } from 'react-use';
import { v4 as uuid } from 'uuid';

import { copyTextToClipboard } from '../../utils/copy-text-to-clipboard/copy-text-to-clipboard';
import { useStableCallback } from '../../utils/hooks';
import { useMe } from '../../utils/hooks/useMe';
import { useAppContext } from '../app-factory/context';
import { Button } from '../button/button';
import { UI_KIT_TEXT_TYPOGRAPHY } from '../constants';
import { Copy } from '../copy/copy';
import { MarkdownExtXContent } from '../markdown/markdown';
import { showNetworkError } from '../notification/actions';
import { RobotCodeLoadable } from '../robot/__code/robot__code-loadable';
import { SimpleText } from '../simple-text/simple-text';
import Spin from '../spin/spin';
import SplitView from '../split-view/split-view';
import { Text } from '../text/text';
import { useWindowMessage } from '../web-problem/useWindowMessage';

import { DirectoryTree, FileIcon } from './__tree/webservice-problem__tree';
import { WebservicePropsProvider } from './context';
import { useSaveProjectMutation, useWebserviceProjectQuery } from './queries';
import { ProblemWebServiceAnswer, WebserviceProblemProps } from './types';
// @ts-ignore
import styles from './webservice-problem.module.scss';

const createHtmlPane = (htmlCode: string): Pane => ({
    content: htmlCode,
    language: 'html',
    name: 'index.html',
});

export const webserviceSavedAction =
    createAction<Pick<ProblemWebServiceAnswer, 'answer_link'>>('WEB_SERVICE_PROBLEM/SAVED');

export let saveWebserviceProjectFiles: () => void;

const WebService = (
    props: WebserviceProblemProps & {
        projectFiles: WebserviceProjectResponse;
    }
) => {
    const myId = useMe('id');
    const projectFiles = props.projectFiles;
    const projectName = props.problem.markup.project_name;
    const cmFilesCode = projectFiles.files;
    // @ts-ignore
    const filesCode = useRef<{ [key: string]: string }>(null as { [key: string]: string });
    if (filesCode.current === null) {
        filesCode.current = { ...projectFiles.latest_answer?.files };
    }
    const setFileCode = (filename: string, code: string) => void (filesCode.current[filename] = code);
    const getPanes = () => {
        const { ['index.html']: indexHtml, ...partials } = Object.assign({}, cmFilesCode, filesCode.current);
        const provideGlobalData = {
            project_name: projectName,
            user_code: pickBy(partials, (_, file) => file.endsWith('.py')),
            user_id: myId,
        };
        partials.script = `window._data=${JSON.stringify(provideGlobalData)};`;
        const code = mustache.render(indexHtml ?? '', {}, partials);
        return [createHtmlPane(code)];
    };
    const frameRef = useRef<HTMLIFrameElement>(null);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const postMessage = (message: unknown) =>
        frameRef.current?.contentWindow?.postMessage(JSON.stringify(message), '*');
    const [frameTitle, setFrameTitle] = useState<string>();
    const [iframeContent, setIframeContent] = useState<{ panes: Pane[] }>(() => ({
        panes: getPanes(),
    }));
    const { waitMessage } = useWindowMessage((data) => {
        if (typeof data.externalLink === 'string') {
            window.open(data.externalLink, '_blank')?.focus();
        }
    });
    const { feynmanDomain } = useAppContext();
    const uploadCode = useAsync(() => postStatic(iframeContent.panes), [iframeContent]);
    const key = uploadCode.value?.key;
    const refreshFrame = useAsync(async () => {
        if (key && iframeLoaded) {
            postMessage({
                // не работает без ts, неправильно парсится в feynman/frame.js
                // [...new URLSearchParams('https://feynman.praktikum.yandex.net:443/static/300249e9-b09d-4c83-ba0e-3f252782f8f0/index.html?ts=1632413856692&liburl=https://code.s3.yandex.net:443/trainer_libs/brackets.js').keys()]
                // = ["https://feynman.praktikum.yandex.net:443/static/300249e9-b09d-4c83-ba0e-3f252782f8f0/index.html?ts", "liburl"]
                src: `https://${feynmanDomain}/static/${key}/index.html?ts=${Date.now()}`,
                staticFilesUrl: `https://${feynmanDomain}/staticfiles`,
            });
            const { title } = await waitMessage<{ title: string | undefined }>((data) => data.onload);
            setFrameTitle(title);
        }
    }, [iframeLoaded, key]);
    const readOnly = props.readOnly === true;
    const isFrameLoading = !iframeLoaded || uploadCode.loading || refreshFrame.loading;
    const canShare = props.me?.id && typeof props.problemLinkId !== 'undefined';
    const [linkShared, setLinkShared] = useState<string | null>(null);
    useEffect(() => setLinkShared(null), [iframeContent]);
    const [shareState, requestShare] = useAsyncFn(async () => {
        const response = await putWebShare({
            panes: iframeContent.panes,
            projectName,
        });
        const indexFile = response.find((f) => f.name === 'index.html');
        if (indexFile) {
            copyTextToClipboard(indexFile.url);
            setLinkShared(indexFile.url);
        }
    }, [iframeContent.panes, props.problemLinkId]);
    const dispatch = useStableCallback(props.dispatch);
    useEffect(() => {
        if (shareState.error) {
            dispatch?.(showNetworkError(shareState.error));
        }
    }, [dispatch, shareState.error]);
    const [file, setFile] = useState<string | null>(null);
    // @ts-ignore
    const initialCmFileCode = cmFilesCode[file];
    const [showDescription, setShowDescription] = useState(true);
    const refreshBrowser = () => setIframeContent({ panes: getPanes() });
    const shouldSetAnswerToEnableSaveButtonRef = useRef(true);
    useEffect(() => void (shouldSetAnswerToEnableSaveButtonRef.current = true), [props.attempt]);
    const [saveWebserviceProject] = useSaveProjectMutation({
        onSuccess: (answerLink) => {
            props.dispatch?.(webserviceSavedAction(answerLink));
        },
    });
    const saveFiles = useStableCallback(() => {
        refreshBrowser();
        const mergedFiles = Object.assign({}, cmFilesCode, filesCode.current);
        delete mergedFiles['index.html'];
        saveWebserviceProject([projectName, mergedFiles]).catch(noop);
    });
    useEffect(() => {
        saveWebserviceProjectFiles = saveFiles;
    }, [saveFiles]);
    const fileExtension = file?.split('.').pop() as 'html' | 'css' | 'py' | undefined;
    return (
        <div className={styles.web}>
            <div className={styles['top-bar']}>
                <Button
                    disabled={isFrameLoading || readOnly}
                    icon={<Refresh />}
                    onClick={refreshBrowser}
                    rounded
                    size={Button.SIZE.S}
                    view={Button.VIEW.ACTION}
                >
                    <SimpleText id="problemWeb.refresh" />
                </Button>
                {canShare && (
                    <Button
                        disabled={isFrameLoading || shareState.loading}
                        icon={<LinkIcon />}
                        onClick={requestShare}
                        rounded
                        size={Button.SIZE.S}
                        view={Button.VIEW.SECONDARY}
                    >
                        <SimpleText id={linkShared ? 'problemWeb.linkCopied' : 'problemWeb.copyLink'} />
                    </Button>
                )}
                {linkShared && (
                    <Button
                        accent={Button.ACCENT.NORMAL}
                        as="a"
                        // @ts-ignore
                        href={linkShared}
                        icon={<ShareArrow />}
                        iconPosition={Button.ICON_POSITION.RIGHT}
                        rounded
                        size={Button.SIZE.S}
                        target="_blank"
                        view={Button.VIEW.PSEUDO}
                    >
                        <FormattedMessage id="problemWeb.openLink" />
                    </Button>
                )}
            </div>
            <div className={styles['main-content']}>
                <SplitView minSizes={[350, 350]}>
                    <div className={styles.left}>
                        <div className={styles.files_editor}>
                            <DirectoryTree onChange={setFile} />
                            <div className={cx(styles.editor, { [styles.editor_teacher]: props.teacherMode })}>
                                {file ? (
                                    <>
                                        <Text
                                            className={styles['file-tab']}
                                            typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphSMedium}
                                        >
                                            <FileIcon filename={file} />
                                            {file}
                                        </Text>
                                        {file.startsWith('images/') ? (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    flexGrow: 1,
                                                }}
                                            >
                                                <div style={{}}>
                                                    <Copy
                                                        text={
                                                            projectFiles.images.find(
                                                                (i) => i.name === file.slice('images/'.length)
                                                            )?.url || ''
                                                        }
                                                    />
                                                </div>
                                                <div
                                                    className={styles.image}
                                                    style={{
                                                        backgroundImage: `url(${
                                                            projectFiles.images.find(
                                                                (i) => i.name === file.slice('images/'.length)
                                                            )?.url || ''
                                                        })`,
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <RobotCodeLoadable
                                                className={styles.code}
                                                fontSize="13px"
                                                initialValue={filesCode.current[file] || initialCmFileCode}
                                                key={file}
                                                onChange={
                                                    readOnly
                                                        ? undefined
                                                        : (_, __, value) => {
                                                              setFileCode(file, value);
                                                              if (shouldSetAnswerToEnableSaveButtonRef.current) {
                                                                  shouldSetAnswerToEnableSaveButtonRef.current = false;
                                                                  props.setAnswer?.({
                                                                      markerAnswer: {
                                                                          user_answer: {
                                                                              // answer_link will be set on save
                                                                          },
                                                                      },
                                                                  });
                                                              }
                                                          }
                                                }
                                                options={{
                                                    mode: {
                                                        html: 'htmlmixed',
                                                        css: 'css',
                                                        py: 'python',
                                                    }[fileExtension || 'html'],
                                                    indentUnit: fileExtension === 'py' ? 4 : 2,
                                                    cursorScrollMargin: showDescription ? 12 : 48,
                                                    readOnly: readOnly || undefined,
                                                }}
                                                paddingBottom={showDescription ? 12 : 48}
                                                paddingRight={12}
                                                robotHints={false}
                                            />
                                        )}
                                    </>
                                ) : null}
                            </div>
                        </div>
                        <div className={styles.info}>
                            <button
                                className={styles['instruction-button']}
                                onClick={() => setShowDescription((s) => !s)}
                            >
                                <h3 className={styles.instruction}>{props.problem.markup.instruction}</h3>
                            </button>
                            {showDescription && (
                                <div className={styles.description}>
                                    <MarkdownExtXContent>{props.problem.markup.description}</MarkdownExtXContent>
                                </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <div className={cx(styles.browser, { [styles.browser_teacher]: props.teacherMode })}>
                            <div className={styles.browser__controls}>
                                <Text
                                    className={styles.browser__address}
                                    typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphSMedium}
                                >
                                    {frameTitle || <FormattedMessage id="problemWeb.browser" />}
                                </Text>
                                <div className={styles.browser__dots} />
                            </div>
                            <div className={styles.browser__content}>
                                <iframe
                                    allow="fullscreen;geolocation;camera;microphone;publickey-credentials-get"
                                    className={styles.browser__frame}
                                    frameBorder="0"
                                    onLoad={() => setIframeLoaded(true)}
                                    ref={frameRef}
                                    src={`https://${feynmanDomain}/staticfiles/frame.html`}
                                />
                            </div>
                        </div>
                    </div>
                </SplitView>
            </div>
        </div>
    );
};

const WebserviceProblemWithProjectData = (props: WebserviceProblemProps) => {
    const projectFiles = useWebserviceProjectQuery(props.problem.markup, {
        refetchOnMount: 'always',
    });
    if (projectFiles.data) {
        return <WebService projectFiles={projectFiles.data} {...props} />;
    }
    return <Spin center />;
};

export const WebserviceProblem = (props: WebserviceProblemProps) => {
    // В статистике учителя не сразу приезжает attempt внутри problem. Костыль key, чтоб показать ответ ученика
    const key = useMemo(uuid, [props.problem]);
    if (props.previewMode) {
        return (
            <div className={cx(styles.preview)}>
                {props.problem.markup.available_files.map((file) => (
                    <div className={styles.preview__file} key={file}>
                        <FileIcon filename={file} />
                        {file}
                    </div>
                ))}
            </div>
        );
    }
    return (
        <WebservicePropsProvider value={props}>
            {/*<WebserviceStoreProvider createStore={createWebserviceStore}>*/}
            <WebserviceProblemWithProjectData {...props} key={key} />
            {/*</WebserviceStoreProvider>*/}
        </WebservicePropsProvider>
    );
};
